import React from "react";
import { Pagination } from "./pagination";
import { usePagination } from "@/hooks/usePagination";
import { useNativePlatform } from "@/hooks/useNativePlatform";

interface PaginatedGridProps<T> {
  data: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  itemsPerPage?: number;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  className?: string;
  emptyMessage?: string;
  campaignCards?: boolean;
  viewMode?: "wide" | "narrow" | "grid";
}

export function PaginatedGrid<T>({
  data,
  renderItem,
  itemsPerPage = 15,
  initialPage,
  onPageChange,
  onItemsPerPageChange,
  className = "",
  emptyMessage = "Inga objekt att visa",
  campaignCards = false,
  viewMode = "wide",
}: PaginatedGridProps<T>) {
  const { isNative } = useNativePlatform();
  const {
    currentData,
    currentPage,
    totalPages,
    itemsPerPage: currentItemsPerPage,
    totalItems,
    setPage,
    setItemsPerPage,
  } = usePagination({
    data,
    initialItemsPerPage: itemsPerPage,
    ...(initialPage != null ? { initialPage } : {}),
  });

  if (data.length === 0) {
    return (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        {emptyMessage}
      </div>
    );
  }

  // Determine grid layout based on view mode
  const getGridLayout = () => {
    if (campaignCards) {
      if (isNative) {
        return "grid grid-cols-2 gap-2.5";
      }
      return "grid-cols-1 md:grid-cols-3";
    }

    if (viewMode === "grid") {
      if (isNative) {
        return "grid-cols-2 gap-2.5";
      }
      return "grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4";
    }

    // Wide and narrow views use 1 column
    return "grid-cols-1";
  };

  // Get container width class based on view mode
  const getContainerWidth = () => {
    if (campaignCards) {
      if (isNative) {
        return "w-full px-0";
      }
      return "w-full px-[10px] sm:px-3 md:px-4";
    }
    return "w-full px-2 sm:px-3 md:px-4";
  };

  const nativeCampaign = campaignCards && isNative;

  return (
    <div className={className}>
      <div className={getContainerWidth()}>
        <div
          className={
            nativeCampaign
              ? "native-campaign-grid grid w-full grid-cols-2 gap-2.5"
              : `grid w-full min-w-0 gap-4 ${getGridLayout()}`
          }
        >
          {currentData.map((item, index) => (
            <div
              key={
                (item as { id?: string; _id?: string })?.id ??
                (item as { id?: string; _id?: string })?._id ??
                index
              }
            >
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      </div>

      {data.length > 0 && (!campaignCards || data.length >= 10) && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={currentItemsPerPage}
          onPageChange={(page) => {
            setPage(page);
            onPageChange?.(page);
          }}
          onItemsPerPageChange={(newItemsPerPage) => {
            setItemsPerPage(newItemsPerPage);
            onItemsPerPageChange?.(newItemsPerPage);
          }}
        />
      )}
    </div>
  );
}

